import { useState, useLayoutEffect } from 'react'
import { useGameStore, TutorialPhase } from '@store/gameStore'
import './TutorialOverlay.css'

// Tutorial step configuration
interface TutorialStep {
    phase: TutorialPhase
    subStep: number
    screen: string
    title: string
    text: string
    highlight?: string  // CSS selector or element ID to highlight
    arrow?: 'up' | 'down' | 'left' | 'right'
    button?: string
    autoAdvance?: boolean
}

const TUTORIAL_STEPS: TutorialStep[] = [
    // Phase: Shop
    { phase: 'shop', subStep: 0, screen: 'shop', title: 'Gratis Geschenk!', text: 'Hier ist ein Willkommenspaket für dich! 🎁', highlight: 'tutorial-free-bundle', arrow: 'down' },

    // Phase: Gacha
    { phase: 'gacha', subStep: 0, screen: 'gacha', title: 'Lootbox öffnen!', text: 'Mal sehen was drin ist! 🎰', highlight: 'gacha-pull-btn', arrow: 'down' },
    { phase: 'gacha', subStep: 1, screen: 'gacha', title: 'Lootbox öffnet...', text: 'Einen Moment Geduld! 🤞' },
    { phase: 'gacha', subStep: 2, screen: 'gacha', title: 'Glückwunsch!', text: 'Du hast ein Item erhalten! Nimm es an. ⚔️', highlight: 'gacha-accept-btn', arrow: 'down' },

    // Phase: Hero
    { phase: 'hero', subStep: 0, screen: 'hero', title: 'Ausrüsten!', text: 'Zieh das Item deinem Helden an! ⚔️', highlight: 'tutorial-equip-slot', arrow: 'down' },
    { phase: 'hero', subStep: 1, screen: 'hero', title: 'Item wählen!', text: 'Wähle dein neues Item aus! ✨', highlight: 'tutorial-inv-item-btn', arrow: 'down' },

    // Phase: Battle
    { phase: 'battle', subStep: 0, screen: 'battle', title: 'Erster Kampf!', text: 'Besiege den Blob! 💥', highlight: 'battle-attack-btn', arrow: 'down' },

    // Phase: Daily
    { phase: 'daily', subStep: 0, screen: 'daily', title: 'Tägliche Belohnung!', text: 'Hol dir dein Geschenk ab! 🎁', highlight: 'daily-claim-btn', arrow: 'down' },
    { phase: 'daily', subStep: 1, screen: 'daily', title: 'Belohnung erhalten!', text: 'Toll! Komm morgen wieder für mehr. 💎', highlight: 'daily-ok-btn', arrow: 'down' },
    { phase: 'daily', subStep: 2, screen: 'daily', title: 'Zur Stadt!', text: 'Gehe zurück zur Stadt-Übersicht! 🏠', highlight: 'nav-city-btn', arrow: 'down' },

    // Phase: Bank
    { phase: 'bank', subStep: 0, screen: 'city', title: 'Oma fragen!', text: 'Besuche die Bank in der Stadt! 💳', highlight: 'nav-bank-btn', arrow: 'down' },
    { phase: 'bank', subStep: 1, screen: 'bank', title: 'Taschengeld!', text: 'Frag Oma nach einem kleinen Zuschuss! 👵', highlight: 'bank-grandma-btn', arrow: 'down' },
    { phase: 'bank', subStep: 2, screen: 'bank', title: 'Danke sagen!', text: 'Nimm das Geld von Oma an! 💕', highlight: 'bank-grandma-ok-btn', arrow: 'down' },

    // Phase: PvP
    { phase: 'pvp', subStep: 0, screen: 'ranking', title: 'PvP Arena!', text: 'Kämpfe um deinen Rang! 🏅', highlight: 'pvp-attack-btn', arrow: 'down' },
]

export function TutorialOverlay() {
    const tutorialPhase = useGameStore(state => state.tutorialPhase)
    const tutorialSubStep = useGameStore(state => state.tutorialSubStep)
    const currentScreen = useGameStore(state => state.currentScreen)
    const setScreen = useGameStore(state => state.setScreen)

    // Find current step
    const currentStep = TUTORIAL_STEPS.find(
        s => s.phase === tutorialPhase && s.subStep === tutorialSubStep
    )

    // Don't render if no active tutorial or wrong screen
    if (!currentStep) return null
    if (currentStep.screen !== currentScreen) {
        // Auto-navigate to correct screen if needed
        if (['shop', 'gacha', 'hero', 'battle', 'daily', 'bank', 'ranking'].includes(currentStep.screen)) {
            // Only navigate if we're in tutorial mode
            if (tutorialPhase !== 'complete' && tutorialPhase !== 'skipped' && tutorialPhase !== 'not-started') {
                setTimeout(() => setScreen(currentStep.screen as any), 100)
            }
        }
        return null
    }

    return (
        <div className="tutorial-overlay">
            {/* Tutorial Card */}
            <div className="tutorial-overlay__card">
                <div className="tutorial-overlay__icon">📚</div>
                <h3 className="tutorial-overlay__title">{currentStep.title}</h3>
                <p className="tutorial-overlay__text">{currentStep.text}</p>

                {/* Progress dots */}
                <div className="tutorial-overlay__progress">
                    {['shop', 'gacha', 'hero', 'battle', 'daily', 'bank', 'pvp'].map((phase) => (
                        <span
                            key={phase}
                            className={`tutorial-overlay__dot ${phase === tutorialPhase ? 'tutorial-overlay__dot--active' :
                                TUTORIAL_STEPS.findIndex(s => s.phase === phase) < TUTORIAL_STEPS.findIndex(s => s.phase === tutorialPhase) ? 'tutorial-overlay__dot--done' : ''
                                }`}
                        />
                    ))}
                </div>
            </div>

            {/* Highlight effect - Skip for shop phase (no extra dimming) */}
            {currentStep.highlight && tutorialPhase !== 'shop' && (
                <TutorialHighlight
                    targetId={currentStep.highlight}
                    arrow={currentStep.arrow}
                />
            )}
        </div>
    )
}

// Component to highlight a target element
function TutorialHighlight({ targetId, arrow }: { targetId: string, arrow?: string }) {
    const [style, setStyle] = useState<React.CSSProperties>({ display: 'none', opacity: 0 })
    const [arrowStyle, setArrowStyle] = useState<React.CSSProperties>({ display: 'none', opacity: 0 })

    useLayoutEffect(() => {
        const updatePosition = () => {
            const element = document.getElementById(targetId) || document.querySelector(`.${targetId}`)
            if (element) {
                const rect = element.getBoundingClientRect()
                if (rect.width === 0) return

                setStyle({
                    top: rect.top - 8,
                    left: rect.left - 8,
                    width: rect.width + 16,
                    height: rect.height + 16,
                    display: 'block',
                    opacity: 1
                })

                if (arrow) {
                    let arrowTop = 0
                    let arrowLeft = rect.left + rect.width / 2
                    let icon = '👇'

                    if (arrow === 'down') {
                        arrowTop = rect.top - 45
                        icon = '👇'
                    } else if (arrow === 'up') {
                        arrowTop = rect.bottom + 15
                        icon = '👆'
                    } else if (arrow === 'left') {
                        arrowLeft = rect.right + 15
                        arrowTop = rect.top + rect.height / 2
                        icon = '👈'
                    } else if (arrow === 'right') {
                        arrowLeft = rect.left - 45
                        arrowTop = rect.top + rect.height / 2
                        icon = '👉'
                    }

                    setArrowStyle({
                        top: arrowTop,
                        left: arrowLeft,
                        display: 'block',
                        opacity: 1
                    })
                    setArrowIcon(icon)
                }
            } else {
                setStyle({ display: 'none', opacity: 0 })
                setArrowStyle({ display: 'none', opacity: 0 })
            }
        }

        updatePosition()
        const interval = setInterval(updatePosition, 100)
        window.addEventListener('resize', updatePosition)
        window.addEventListener('scroll', updatePosition, true)

        return () => {
            clearInterval(interval)
            window.removeEventListener('resize', updatePosition)
            window.removeEventListener('scroll', updatePosition, true)
        }
    }, [targetId, arrow])

    const [arrowIcon, setArrowIcon] = useState('👇')

    return (
        <>
            <div className="tutorial-highlight" style={style} />
            {arrow && (
                <div className={`tutorial-arrow tutorial-arrow--${arrow}`} style={arrowStyle}>
                    {arrowIcon}
                </div>
            )}
        </>
    )
}
